import * as React from 'react';
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  type AccountInfo
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

import { setAuthTokenProvider } from '@cdf/api-client-react';
import { embedded, entraEnabled, loginRequest } from '@/lib/auth-config';

/**
 * Tiered authentication provider per entra-sso-embedded skill §5.
 *
 * State machine: silent SSO once → popup when embedded → redirect standalone.
 *
 * - `ssoSilent` runs exactly once per boot (ref-guarded) — retry loops
 *   spawn iframe storms.
 * - No automatic `loginPopup` during boot: popups without a user gesture
 *   are blocked by every modern browser. The interactive popup fires from
 *   the sign-in button click.
 * - The effect waits for `inProgress === InteractionStatus.None` so it never
 *   races MSAL's own redirect/popup processing.
 * - API bearer tokens flow through `setAuthTokenProvider`
 *   (`acquireTokenSilent` → `idToken`), keeping the same contract consumers
 *   rely on.
 */

export interface AuthContextValue {
  enabled: boolean;
  ready: boolean;
  isAuthenticated: boolean;
  account: { name: string; email: string | null } | null;
  authError: string | null;
  popupBlocked: boolean;
  clearAuthError: () => void;
  login: () => void;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue>({
  enabled: false,
  ready: true,
  isAuthenticated: false,
  account: null,
  authError: null,
  popupBlocked: false,
  clearAuthError: () => undefined,
  login: () => undefined,
  logout: () => undefined
});

export function useAuth(): AuthContextValue {
  return React.useContext(AuthContext);
}

function accountDisplay(account: AccountInfo): { name: string; email: string | null } {
  return {
    name: account.name ?? account.username,
    email: account.username || null
  };
}

function MsalAuthProvider({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const [isLoading, setIsLoading] = React.useState(true);
  const [authError, setAuthError] = React.useState<string | null>(null);
  const [popupBlocked, setPopupBlocked] = React.useState(false);
  const ssoAttempted = React.useRef(false);

  const account = React.useMemo(
    () => instance.getActiveAccount() ?? accounts[0] ?? null,
    [accounts, instance]
  );

  // Tiered session initialization: ssoSilent once → acquireTokenSilent.
  React.useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    let cancelled = false;

    async function initializeSession() {
      setIsLoading(true);
      try {
        if (!account) {
          // One-shot silent SSO: hidden iframe + existing Entra session
          // cookie. Works in embedded previews without any redirect.
          if (!ssoAttempted.current) {
            ssoAttempted.current = true;
            try {
              const cached = instance.getAllAccounts()[0];
              const result = await instance.ssoSilent({
                ...loginRequest,
                ...(cached?.username ? { loginHint: cached.username } : {})
              });
              instance.setActiveAccount(result.account);
              return; // account change re-runs this effect
            } catch {
              // interaction_required / monitor_window_timeout — fall through
              // to the sign-in page for a user-initiated interactive login.
            }
          }
          return;
        }
        instance.setActiveAccount(account);
        await instance.acquireTokenSilent({ ...loginRequest, account });
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          if (embedded) {
            // Redirects are illegal inside an iframe — recover via popup.
            try {
              const result = await instance.acquireTokenPopup({
                ...loginRequest,
                account: account ?? undefined
              });
              instance.setActiveAccount(result.account);
            } catch {
              // Popup blocked without a gesture — the sign-in page provides
              // a user-initiated retry.
            }
          } else {
            await instance.acquireTokenRedirect({
              ...loginRequest,
              account: account ?? undefined
            });
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void initializeSession();
    return () => {
      cancelled = true;
    };
  }, [account, inProgress, instance]);

  // Wire API bearer token provider: acquireTokenSilent → idToken.
  React.useEffect(() => {
    if (!account) {
      setAuthTokenProvider(null);
      return;
    }
    setAuthTokenProvider(async () => {
      try {
        const result = await instance.acquireTokenSilent({
          ...loginRequest,
          account
        });
        return result.idToken || null;
      } catch {
        return null;
      }
    });
    return () => setAuthTokenProvider(null);
  }, [instance, account]);

  // User-gesture-driven interactive sign-in (from the sign-in button).
  const signInWithMicrosoft = React.useCallback(async () => {
    setAuthError(null);
    if (embedded) {
      const result = await instance.loginPopup({
        ...loginRequest,
        prompt: 'select_account'
      });
      instance.setActiveAccount(result.account);
      return;
    }
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  // Sign-out branches the same way: popup embedded, redirect standalone.
  const signOutMicrosoft = React.useCallback(async () => {
    if (!account) return;
    setAuthTokenProvider(null);
    if (embedded) {
      await instance.logoutPopup({ account });
    } else {
      await instance.logoutRedirect({ account });
    }
  }, [account, instance]);

  const clearAuthError = React.useCallback(() => {
    setAuthError(null);
    setPopupBlocked(false);
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      enabled: true,
      ready: !isLoading,
      isAuthenticated: Boolean(account),
      account: account ? accountDisplay(account) : null,
      authError,
      popupBlocked,
      clearAuthError,
      login: () => {
        setPopupBlocked(false);
        setAuthError(null);
        void signInWithMicrosoft().catch((err: unknown) => {
          const errorCode = (err as { errorCode?: string })?.errorCode;
          if (errorCode === 'popup_window_error' || errorCode === 'empty_window_error') {
            setPopupBlocked(true);
          } else if (errorCode !== 'user_cancelled') {
            setAuthError(err instanceof Error ? err.message : 'Microsoft sign-in failed');
          }
        });
      },
      logout: () => {
        void signOutMicrosoft().catch(() => {
          // Sign-out errors are non-critical; the session clears on reload.
        });
      }
    }),
    [account, isLoading, authError, popupBlocked, clearAuthError, signInWithMicrosoft, signOutMicrosoft]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  if (!entraEnabled) {
    // Auth disabled: run unauthenticated (honest local-dev mode).
    return (
      <AuthContext.Provider
        value={{
          enabled: false,
          ready: true,
          isAuthenticated: false,
          account: null,
          authError: null,
          popupBlocked: false,
          clearAuthError: () => undefined,
          login: () => undefined,
          logout: () => undefined
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return <MsalAuthProvider>{children}</MsalAuthProvider>;
}
